import type { InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import { apiClient } from './client';

// Enhanced interceptors with additional functionality

// Request logging interceptor (only in development)
export const requestLoggingInterceptor = {
  onFulfilled: (config: InternalAxiosRequestConfig) => {
    if (import.meta.env.DEV && import.meta.env.VITE_LOG_LEVEL === 'debug') {
      console.log(`[API] ${config.method?.toUpperCase()} ${config.url}`);
    }
    return config;
  },
  onRejected: (error: unknown) => {
    if (import.meta.env.DEV) {
      console.error('[API] Request Error:', error);
    }
    return Promise.reject(error);
  },
};

// Response logging interceptor (only in development)
export const responseLoggingInterceptor = {
  onFulfilled: (response: AxiosResponse) => {
    if (import.meta.env.DEV && import.meta.env.VITE_LOG_LEVEL === 'debug') {
      console.log(`[API] ${response.status} ${response.config.url}`);
    }
    return response;
  },
  onRejected: (error: unknown) => {
    if (import.meta.env.DEV) {
      const axiosError = error as { response?: { status: number; data?: { message: string } }; message?: string; config?: { url: string } };
      console.error('[API] Response Error:', {
        status: axiosError.response?.status,
        message: axiosError.response?.data?.message || axiosError.message,
        url: axiosError.config?.url,
      });
    }
    return Promise.reject(error);
  },
};

// Loading state interceptor
let activeRequests = 0;
export const loadingInterceptor = {
  request: {
    onFulfilled: (config: InternalAxiosRequestConfig) => {
      activeRequests++;
      // Trigger loading state
      window.dispatchEvent(new CustomEvent('api:loading:start', { detail: { activeRequests } }));
      return config;
    },
    onRejected: (error: unknown) => {
      activeRequests = Math.max(0, activeRequests - 1);
      if (activeRequests === 0) {
        window.dispatchEvent(new CustomEvent('api:loading:end'));
      }
      return Promise.reject(error);
    },
  },
  response: {
    onFulfilled: (response: AxiosResponse) => {
      activeRequests = Math.max(0, activeRequests - 1);
      if (activeRequests === 0) {
        window.dispatchEvent(new CustomEvent('api:loading:end'));
      }
      return response;
    },
    onRejected: (error: unknown) => {
      activeRequests = Math.max(0, activeRequests - 1);
      if (activeRequests === 0) {
        window.dispatchEvent(new CustomEvent('api:loading:end'));
      }
      return Promise.reject(error);
    },
  },
};

// Setup interceptors (call this in your app initialization)
export const setupInterceptors = () => {
  // Add request interceptors
  apiClient.interceptors.request.use(
    loadingInterceptor.request.onFulfilled,
    loadingInterceptor.request.onRejected
  );

  // Add response interceptors
  apiClient.interceptors.response.use(
    loadingInterceptor.response.onFulfilled,
    loadingInterceptor.response.onRejected
  );

  // Add logging interceptors only in development with debug level
  if (import.meta.env.DEV && import.meta.env.VITE_LOG_LEVEL === 'debug') {
    apiClient.interceptors.request.use(
      requestLoggingInterceptor.onFulfilled,
      requestLoggingInterceptor.onRejected
    );
    apiClient.interceptors.response.use(
      responseLoggingInterceptor.onFulfilled,
      responseLoggingInterceptor.onRejected
    );
  }
};
